import { createContext, type ReactNode, useContext } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'
import { canAccess } from '../utils/fuAdminAccess'

const fragments = {
  FuViewerProvider_admin: graphql(`
    fragment FuViewerProvider_admin on FuAdmin {
      id
      name
      roles
      companyCountries
      schoolCountries
    }
  `),
}

type AdminAccessRightsContextProps = {
  isAllowedInstitutionEdit: boolean
  isAllowedInstitutionLink: boolean
  isAllowedCreatePaymentUrl: boolean
  isAllowedPartnerApprove: boolean
  isAllowedPartnerDelete: boolean
  isAllowedIntrowActivation: boolean
  isAllowedDemoCompany: boolean
  isAllowedApproveOrRejectSchool: boolean
  isAllowedInstitutionMemberActions: boolean
  isAllowedBasicMemberActions: boolean
}

type AdminAccessRightsProps = {
  children: ReactNode
  viewer: FragmentType<typeof fragments.FuViewerProvider_admin> | null
}

export const AdminAccessRightsContext = createContext<AdminAccessRightsContextProps>({
  isAllowedInstitutionLink: false,
  isAllowedInstitutionEdit: false,
  isAllowedCreatePaymentUrl: false,
  isAllowedPartnerApprove: false,
  isAllowedPartnerDelete: false,
  isAllowedIntrowActivation: false,
  isAllowedDemoCompany: false,
  isAllowedApproveOrRejectSchool: false,
  isAllowedInstitutionMemberActions: false,
  isAllowedBasicMemberActions: false,
})

export const AdminAccessRightsProvider = ({
  children,
  viewer: _viewer,
}: AdminAccessRightsProps) => {
  const viewer = getFragmentData(fragments.FuViewerProvider_admin, _viewer)
  const roles = viewer?.roles ?? []

  const permissions: AdminAccessRightsContextProps = {
    isAllowedInstitutionEdit: canAccess(roles, 'institutionEdit'),
    isAllowedPartnerApprove: canAccess(roles, 'partnerApprove'),
    isAllowedPartnerDelete: canAccess(roles, 'partnerDelete'),
    isAllowedIntrowActivation: canAccess(roles, 'introwActivation'),
    isAllowedCreatePaymentUrl: canAccess(roles, 'createPaymentUrl'),
    isAllowedInstitutionLink: canAccess(roles, 'institutionLink'),
    isAllowedDemoCompany: canAccess(roles, 'demoCompany'),
    isAllowedApproveOrRejectSchool: canAccess(roles, 'approveOrRejectSchool'),
    isAllowedInstitutionMemberActions: canAccess(roles, 'institutionMemberActions'),
    isAllowedBasicMemberActions: canAccess(roles, 'basicMemberActions'),
  }

  return (
    <AdminAccessRightsContext.Provider value={permissions}>
      {children}
    </AdminAccessRightsContext.Provider>
  )
}

export const useAdminAccessRights = () => useContext(AdminAccessRightsContext)
